import React from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { mainGreen } from '../../../utils/color'
import { accountTypeLabel, DEFAULT_CASH_SOURCE, formatMoney, sectionSx } from '../cashflowUtils'
import { EmptyRow, MoneyStat } from './CashflowCommon'

const incomeAmount = (income) => Number(income?.amount) || 0

export default function IncomesTab({
	bankAccounts,
	canManageIncome,
	closeIncomeDialog,
	deleteIncome,
	editingIncomeId,
	incomeDialogOpen,
	incomeForm,
	openCreateIncome,
	saveIncome,
	setIncomeForm,
	startEditIncome,
	summary,
}) {
	const bankOnlyAccounts = bankAccounts.filter((account) => (account.accountType || 'bank') === 'bank')
	const editableIncomes = (summary?.income?.items || []).filter((income) => income.receiptType === 'abnormal')
	const abnormalCashIncome = editableIncomes
		.filter((income) => income.source === 'cash')
		.reduce((total, income) => total + incomeAmount(income), 0)
	const abnormalBankIncome = editableIncomes
		.filter((income) => income.source !== 'cash')
		.reduce((total, income) => total + incomeAmount(income), 0)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
			<Dialog open={incomeDialogOpen} onClose={closeIncomeDialog} fullWidth maxWidth='md'>
				<DialogTitle>{editingIncomeId ? 'Sửa khoản thu bất thường' : 'Tạo khoản thu bất thường'}</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={1.75} sx={{ pt: 0.5 }}>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
							<FormControl fullWidth size='small'>
								<InputLabel>Tài khoản thu</InputLabel>
								<Select
									label='Tài khoản thu'
									value={incomeForm.bankAccount}
									onChange={(event) => setIncomeForm((prev) => ({ ...prev, bankAccount: event.target.value }))}>
									<MenuItem value={DEFAULT_CASH_SOURCE}>Tiền mặt mặc định</MenuItem>
									{bankOnlyAccounts.map((account) => (
										<MenuItem key={account._id} value={account._id}>
											{account.name} - {accountTypeLabel(account)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								fullWidth
								size='small'
								label='Số tiền'
								value={incomeForm.amount}
								onChange={(event) => setIncomeForm((prev) => ({ ...prev, amount: event.target.value }))}
							/>
						</Stack>
						<TextField
							fullWidth
							size='small'
							label='Nội dung'
							multiline
							minRows={2}
							value={incomeForm.content}
							onChange={(event) => setIncomeForm((prev) => ({ ...prev, content: event.target.value }))}
						/>
						<TextField
							fullWidth
							size='small'
							type='date'
							label='Ngày gán TK đơn'
							InputLabelProps={{ shrink: true }}
							value={incomeForm.appliesToReportDate}
							onChange={(event) => setIncomeForm((prev) => ({ ...prev, appliesToReportDate: event.target.value }))}
						/>
						<Box>
							<Button component='label' variant='outlined' startIcon={<UploadFileIcon />}>
								{incomeForm.photos?.length > 0 ? `${incomeForm.photos.length} ảnh mới` : 'Đính kèm ảnh'}
								<input
									hidden
									multiple
									type='file'
									accept='image/*'
									onChange={(event) => setIncomeForm((prev) => ({ ...prev, photos: event.target.files }))}
								/>
							</Button>
						</Box>
						{incomeForm.existingPhotos?.length > 0 && (
							<Typography sx={{ color: '#637381', fontSize: 13 }}>
								Đang giữ {incomeForm.existingPhotos.length} ảnh cũ
							</Typography>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button startIcon={<CloseIcon />} onClick={closeIncomeDialog}>
						Hủy
					</Button>
					<Button
						variant='contained'
						startIcon={<SaveIcon />}
						disabled={!canManageIncome}
						sx={{ background: mainGreen }}
						onClick={saveIncome}>
						{editingIncomeId ? 'Cập nhật khoản thu bất thường' : 'Lưu khoản thu bất thường'}
					</Button>
				</DialogActions>
			</Dialog>

			<Paper elevation={0} sx={sectionSx}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={1}
					alignItems={{ xs: 'stretch', md: 'center' }}
					justifyContent='space-between'
					sx={{ mb: 2 }}>
					<Typography sx={{ fontWeight: 800 }}>Khoản thu bất thường trong ngày</Typography>
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						disabled={!canManageIncome}
						sx={{ background: mainGreen }}
						onClick={openCreateIncome}>
						Tạo khoản thu bất thường
					</Button>
				</Stack>
				<Box
					className='cashflow-section-bleed'
					sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, mb: 2 }}>
					<MoneyStat compact label='Tiền mặt bất thường' value={abnormalCashIncome} color={mainGreen} />
					<MoneyStat compact label='Ngân hàng bất thường' value={abnormalBankIncome} color='#1976d2' />
					<MoneyStat compact label='Tổng bất thường' value={abnormalCashIncome + abnormalBankIncome} color='#B76E00' />
				</Box>
				<Box className='cashflow-table-shell cashflow-scroll'>
					<Table size='small' sx={{ minWidth: 880 }}>
						<TableHead>
							<TableRow>
								<TableCell>Nội dung</TableCell>
								<TableCell>Tài khoản</TableCell>
								<TableCell>Ngày gán TK đơn</TableCell>
								<TableCell align='right'>Số tiền</TableCell>
								<TableCell align='right'></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{editableIncomes.length === 0 && <EmptyRow colSpan={5} text='Chưa có khoản thu bất thường' />}
							{editableIncomes.map((income) => (
								<TableRow key={income._id} selected={editingIncomeId === income._id}>
									<TableCell>
										<Typography sx={{ fontWeight: 800 }}>{income.content}</Typography>
									</TableCell>
									<TableCell>
										{income.source === 'cash' ? 'Tiền mặt mặc định' : income.bankAccount?.name || 'Tài khoản đã ngưng'}
									</TableCell>
									<TableCell>{income.appliesToReportDate || income.reportDate || '-'}</TableCell>
									<TableCell align='right' sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
										{formatMoney(income.amount)}
									</TableCell>
									<TableCell align='right'>
										{canManageIncome && (
											<Stack direction='row' spacing={0.5} justifyContent='flex-end'>
												<IconButton color='primary' size='small' onClick={() => startEditIncome(income)}>
													<EditIcon fontSize='small' />
												</IconButton>
												<IconButton color='error' size='small' onClick={() => deleteIncome(income._id)}>
													<DeleteIcon fontSize='small' />
												</IconButton>
											</Stack>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Paper>
		</Box>
	)
}
